import { useCallback, useState } from 'react'
import type {
  AgentMode,
  ClaudeSession,
  Command,
  ConversationTurn,
  ConversationTurnEntry,
  UserOption,
  UUID,
} from '@/index'
import { CommandType, ErrorRecoveryType } from '@/index'
import {
  getSessionEventsSortedNewestFirst,
  isNotInteractable,
} from '@/features/events/conversation/state/types'
import { useCrypto } from '@/infra/crypto/context'
import { buildCommandPayload, commandClient, encryptCommand } from '../api'
import { useSubmissionState } from '../state'

interface UseCommandSubmitterParams {
  readonly session: ClaudeSession | null
  readonly conversationTurn: ConversationTurn | null
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly createNewTurn?: (
    session: ClaudeSession,
    userPromptId: UUID,
    userCommand: Command,
  ) => void
  readonly onSuccess?: () => void
  readonly onError?: (error: Error) => void
}

export interface UseCommandSubmitterReturn {
  readonly isSubmitting: boolean
  readonly error: string | null

  readonly submitInject: (prompt: string, agentMode?: AgentMode) => Promise<void>

  readonly submitReply: (prompt: string, agentMode?: AgentMode) => Promise<void>

  readonly submitPause: () => Promise<void>

  readonly submitResume: () => Promise<void>

  readonly submitUserOption: (
    event: ConversationTurnEntry,
    option: UserOption,
    customText: string | null,
    agentMode?: AgentMode,
  ) => Promise<void>

  readonly submitQuestionnaireAnswers: <T extends object>(
    event: ConversationTurnEntry,
    answers: T,
  ) => Promise<void>

  readonly submitErrorRecovery: (
    event: ConversationTurnEntry,
    recoveryType: ErrorRecoveryType,
  ) => Promise<void>

  readonly clearError: () => void
}

export function useCommandSubmitter(params: UseCommandSubmitterParams): UseCommandSubmitterReturn {
  const { session, conversationTurn, recordCommand, createNewTurn, onSuccess, onError } = params
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { setSubmitting: setGlobalSubmitting } = useSubmissionState()
  const { encrypt, activeKeyVersion, isReady } = useCrypto()

  const validateSession = useCallback((): void => {
    if (!session) {
      throw new Error('No session selected')
    }
    if (isNotInteractable(session)) {
      throw new Error(
        'Cannot submit commands for non-interactive sessions. This session does not have an active tmux session.',
      )
    }
    if (!conversationTurn) {
      throw new Error('No conversation turn available')
    }
  }, [session, conversationTurn])

  const handleNewTurnCreation = useCallback(
    (isNewTurn: boolean, newUserPromptId: UUID | null, command: Command): void => {
      if (isNewTurn && newUserPromptId && createNewTurn && session) {
        createNewTurn(session, newUserPromptId, command)
      }
    },
    [createNewTurn, session],
  )

  const handleSubmissionSuccess = useCallback(
    (isNewTurn: boolean, command: Command): void => {
      if (!isNewTurn && conversationTurn && session) {
        recordCommand(session.sessionId, conversationTurn.userPromptId, command)
      }
      onSuccess?.()
    },
    [conversationTurn, recordCommand, session, onSuccess],
  )

  const handleSubmissionError = useCallback(
    (err: unknown): Error => {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(errorMessage)
      const error = err instanceof Error ? err : new Error(errorMessage)
      onError?.(error)
      return error
    },
    [onError],
  )

  const executeSubmission = useCallback(
    async (command: Command, isNewTurn: boolean, newUserPromptId: UUID | null): Promise<void> => {
      validateSession()

      if (!session) {
        throw new Error('No session selected')
      }

      if (!conversationTurn) {
        throw new Error('No conversation turn available')
      }

      if (!isReady || activeKeyVersion === null) {
        throw new Error('Encryption is not available. Cannot submit commands without encryption.')
      }

      setIsSubmitting(true)
      setGlobalSubmitting(conversationTurn.userPromptId, session.sessionId, true)
      setError(null)

      let errorToThrow: Error | null = null

      try {
        handleNewTurnCreation(isNewTurn, newUserPromptId, command)

        const payload = buildCommandPayload(
          command,
          session,
          isNewTurn ? null : conversationTurn.userPromptId,
          newUserPromptId,
        )

        const finalPayload = await encryptCommand(payload, activeKeyVersion, encrypt)

        const result = await commandClient.submit(finalPayload)

        if (result.success) {
          handleSubmissionSuccess(isNewTurn, command)
        } else {
          errorToThrow = new Error(result.message)
          setError(result.message)
          onError?.(errorToThrow)
        }
      } catch (err) {
        errorToThrow = handleSubmissionError(err)
      } finally {
        setIsSubmitting(false)
        setGlobalSubmitting(conversationTurn.userPromptId, session.sessionId, false)
      }

      if (errorToThrow) {
        throw errorToThrow
      }
    },
    [
      validateSession,
      conversationTurn,
      session,
      handleNewTurnCreation,
      handleSubmissionSuccess,
      handleSubmissionError,
      onError,
      setGlobalSubmitting,
      encrypt,
      activeKeyVersion,
      isReady,
    ],
  )

  const getLatestEvent = useCallback((): string => {
    if (conversationTurn && conversationTurn.events.length > 0) {
      const latestEvent = conversationTurn.events.at(-1)
      if (latestEvent) {
        return latestEvent.eventId
      }
    }

    if (!session) {
      throw new Error('No session selected')
    }

    const allSessionEvents = getSessionEventsSortedNewestFirst(session)
    if (allSessionEvents.length === 0) {
      throw new Error('No events in session')
    }
    const firstEvent = allSessionEvents[0]
    if (!firstEvent) {
      throw new Error('Cannot retrieve latest event from session')
    }
    return firstEvent.eventId
  }, [conversationTurn, session])

  const submitInject = useCallback(
    async (prompt: string, agentMode?: AgentMode): Promise<void> => {
      const refEventId = getLatestEvent()
      const command = buildUserInputCommand(
        CommandType.INJECT_PROMPT,
        refEventId,
        prompt,
        agentMode,
      )
      await executeSubmission(command, false, null)
    },
    [getLatestEvent, executeSubmission],
  )

  // submitReply is only callable when canReply is true (last event is AGENT_REPLY),
  // so it always starts a new conversation turn. Same-turn prompts use submitInject.
  const submitReply = useCallback(
    async (prompt: string, agentMode?: AgentMode): Promise<void> => {
      const newUserPromptId = crypto.randomUUID()
      const refEventId = getLatestEvent()
      const command = buildUserInputCommand(
        CommandType.SUBMIT_PROMPT,
        refEventId,
        prompt,
        agentMode,
      )
      await executeSubmission(command, true, newUserPromptId)
    },
    [getLatestEvent, executeSubmission],
  )

  const submitPause = useCallback(async (): Promise<void> => {
    const refEventId = getLatestEvent()
    const command = buildPauseCommand(refEventId)
    await executeSubmission(command, false, null)
  }, [getLatestEvent, executeSubmission])

  const submitResume = useCallback(async (): Promise<void> => {
    const refEventId = getLatestEvent()
    const command = buildResumeCommand(refEventId)
    await executeSubmission(command, false, null)
  }, [getLatestEvent, executeSubmission])

  const submitUserOption = useCallback(
    async (
      event: ConversationTurnEntry,
      option: UserOption,
      customText: string | null,
      agentMode?: AgentMode,
    ): Promise<void> => {
      const command = buildUserOptionCommand(event.eventId, option, customText, agentMode)
      await executeSubmission(command, false, null)
    },
    [executeSubmission],
  )

  const submitQuestionnaireAnswers = useCallback(
    async <T extends object>(event: ConversationTurnEntry, answers: T): Promise<void> => {
      const command = buildQuestionnaireAnswersCommand(event.eventId, answers)
      await executeSubmission(command, false, null)
    },
    [executeSubmission],
  )

  const submitErrorRecovery = useCallback(
    async (event: ConversationTurnEntry, recoveryType: ErrorRecoveryType): Promise<void> => {
      const command = buildErrorRecoveryCommand(event.eventId, recoveryType)
      await executeSubmission(command, false, null)
    },
    [executeSubmission],
  )

  const clearError = useCallback((): void => {
    setError(null)
  }, [])

  return {
    submitInject,
    submitReply,
    submitPause,
    submitResume,
    submitUserOption,
    submitQuestionnaireAnswers,
    submitErrorRecovery,
    isSubmitting,
    error,
    clearError,
  }
}

function buildUserInputCommand(
  commandType: CommandType.SUBMIT_PROMPT | CommandType.INJECT_PROMPT,
  refEventId: UUID,
  prompt: string,
  agentMode?: AgentMode,
): Command {
  return {
    commandType,
    userCommandId: crypto.randomUUID(),
    refEventId,
    timestamp: new Date().toISOString(),
    userOption: null,
    userPrompt: prompt,
    agentMode: agentMode ?? null,
    userAnswers: null,
    errorRecovery: null,
  }
}

function buildPauseCommand(refEventId: UUID): Command {
  return {
    commandType: CommandType.INTERRUPT_AGENT,
    userCommandId: crypto.randomUUID(),
    refEventId,
    timestamp: new Date().toISOString(),
    userOption: null,
    userPrompt: null,
    agentMode: null,
    userAnswers: null,
    errorRecovery: null,
  }
}

function buildResumeCommand(refEventId: UUID): Command {
  return {
    commandType: CommandType.RESUME_AGENT,
    userCommandId: crypto.randomUUID(),
    refEventId,
    timestamp: new Date().toISOString(),
    userOption: null,
    userPrompt: null,
    agentMode: null,
    userAnswers: null,
    errorRecovery: null,
  }
}

function buildUserOptionCommand(
  refEventId: UUID,
  option: UserOption,
  customText: string | null,
  agentMode?: AgentMode,
): Command {
  const userPrompt = customText !== null && customText.length > 0 ? customText : null

  return {
    commandType: CommandType.SUBMIT_OPTION,
    userCommandId: crypto.randomUUID(),
    refEventId,
    timestamp: new Date().toISOString(),
    userOption: option,
    userPrompt,
    agentMode: agentMode ?? null,
    userAnswers: null,
    errorRecovery: null,
  }
}

function buildQuestionnaireAnswersCommand<T extends object>(refEventId: UUID, answers: T): Command {
  return {
    commandType: CommandType.SUBMIT_ANSWERS,
    userCommandId: crypto.randomUUID(),
    refEventId,
    timestamp: new Date().toISOString(),
    userOption: null,
    userPrompt: null,
    agentMode: null,
    userAnswers: answers as Record<string, unknown>,
    errorRecovery: null,
  }
}

function buildErrorRecoveryCommand(refEventId: UUID, recoveryType: ErrorRecoveryType): Command {
  return {
    commandType: CommandType.ERROR_RECOVERY,
    userCommandId: crypto.randomUUID(),
    refEventId,
    timestamp: new Date().toISOString(),
    userOption: null,
    userPrompt: null,
    agentMode: null,
    userAnswers: null,
    errorRecovery: recoveryType,
  }
}
